import type { QueryClient } from '@tanstack/react-query';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import { getConfiguration } from '@services/configurationManager';
import {
  SPECTRAL_WORKSPACE_QUERY_GC_TIME,
  SPECTRAL_WORKSPACE_QUERY_STALE_TIME,
} from './constants';
import { configurationQueryKey } from './keys';

export const configurationFetchOptions = (sessionId: number) => ({
  queryKey: configurationQueryKey(String(sessionId)),
  queryFn: async (): Promise<DeviceConfigurationApi> => {
    const { data } = await getConfiguration(sessionId);
    return data;
  },
  staleTime: SPECTRAL_WORKSPACE_QUERY_STALE_TIME,
  gcTime: SPECTRAL_WORKSPACE_QUERY_GC_TIME,
  refetchOnWindowFocus: false,
});

export const getCachedSessionConfiguration = (
  queryClient: QueryClient,
  sessionId: number
) => {
  const queryKey = configurationQueryKey(String(sessionId));
  const query = queryClient.getQueryCache().find({ queryKey });

  // Prefer isStaleByTime: Query.isStale() ignores staleTime when there are no observers.
  // staleTime lives on QueryObserverOptions; Query.options is typed as QueryOptions.
  const optionStaleTime = (query?.options as { staleTime?: unknown } | undefined)
    ?.staleTime;
  const staleTime =
    typeof optionStaleTime === 'number'
      ? optionStaleTime
      : SPECTRAL_WORKSPACE_QUERY_STALE_TIME;

  if (!query || query.isStaleByTime(staleTime)) {
    return undefined;
  }

  return query.state.data as DeviceConfigurationApi | undefined;
};

export const prefetchSessionConfigurations = async (
  queryClient: QueryClient,
  sessionIds: number[]
) => {
  const uniqueIds = [...new Set(sessionIds)];

  await Promise.all(
    uniqueIds.map(sessionId =>
      queryClient.fetchQuery(configurationFetchOptions(sessionId))
    )
  );
};

export const fetchSessionConfiguration = (
  queryClient: QueryClient,
  sessionId: number
) => queryClient.fetchQuery(configurationFetchOptions(sessionId));
